/**
 * CachedCompanyProfileState: preload companies cache from server PreloadedContacts,
 * using the $type discriminator emitted by the backend.
 */

import { CompanyDto } from "./types";
import { ReservationCompanyCache } from "./model.ReservationCompany";

// Server-emitted DTOs (subset) for PreloadedContacts with $type discriminator
export interface PersonCreditDto {
  CreditId: string;
  Amount: number;
  ExpiresOn: string;
}

export interface ReservationCompanyCacheDto {
  CompanyId: string;
  ContactId: string | null;
  Name: string;
  Credits?: PersonCreditDto[];
}

export type PreloadedContactItemDto =
  | {
      $type: "Person";
      ContactId: string;
      PersonId: string;
      // Person omitted; person caching handled elsewhere
    }
  | {
      $type: "Company";
      ContactId: string;
      CompanyId: string;
      Company: ReservationCompanyCacheDto;
    };

/**
 * Initialize the provided ReservationCompanyCache with company details extracted from
 * the server-side PreloadedContacts payload. Person contacts are ignored.
 */
export function initializeCompaniesCacheFromPreloadedContacts(
  preloadedContacts: { [contactId: string]: PreloadedContactItemDto } | null | undefined,
  companyCache: ReservationCompanyCache
): void {
  if (!preloadedContacts || !companyCache) {
    return;
  }

  const companies: { [companyId: string]: CompanyDto } = {};

  Object.values(preloadedContacts).forEach(contact => {
    if ((contact as any).$type === "Company") {
      const c = contact as Extract<PreloadedContactItemDto, { $type: "Company" }>;
      const dto = c.Company;
      companies[c.CompanyId] = {
        CompanyId: dto.CompanyId,
        ContactId: dto.ContactId ?? null,
        Name: dto.Name
      };
    }
  });

  if (Object.keys(companies).length > 0) {
    companyCache.preloadCompanyDetails(companies);
  }
}

/**
 * Convenience helper: build a ReservationCompanyCache, preload it, and return it.
 * Useful when wiring up a ReservationState instance.
 */
export function buildCompanyCacheFromPreloadedContacts(
  preloadedContacts: { [contactId: string]: PreloadedContactItemDto } | null | undefined,
  onError?: (error: string) => void
): ReservationCompanyCache {
  const cache = new ReservationCompanyCache(onError);
  initializeCompaniesCacheFromPreloadedContacts(preloadedContacts, cache);
  return cache;
}